import * as requestLeaseContractStatuses from 'constants/enums/requestLeaseContractStatuses'
import type {RequestLeaseContractStatus} from 'constants/enums/requestLeaseContractStatuses'
import * as requestLeaseStatuses from 'constants/enums/requestLeaseStatuses'
import type {RequestLeaseStatus} from 'constants/enums/requestLeaseStatuses'
import * as roles from 'constants/enums/roles'
import {REQUEST_LEASE_CONTRACT_GENERATION_RULES} from './requestLeaseContracts'

type User = {
  id: string
  role: roles.Role | null
}

type RequestApproval = {
  id: number
  createdById: string
  assistantId: string | null
}

type RequestDocumentation = {
  id: number
  createdById: string
  assistantId: string | null | undefined
  managerId: string
}

type RequestLease = {
  id: number
  status: RequestLeaseStatus
  createdById: string
  assistantId: string | null | undefined
  managerId: string
  assigneeId: string
}

type RequestLeaseContract = {
  contractStatus: RequestLeaseContractStatus
  finalFileId: string | null
}

export const isAdmin = (user: User) => user.role === roles.A

export const isUserRequestApproval = (user: User) =>
  user.role &&
  (roles.REQUEST_APPROVAL_USER_ROLES as readonly string[]).includes(user.role)
export const canListRequestApprovals = (user: User) =>
  isAdmin(user) || isUserRequestApproval(user)

export const isAssistant = (
  user: User,
  request: RequestApproval | RequestDocumentation | RequestLease,
) => user.id === request.assistantId

export const isCreatedBy = (
  user: User,
  request: RequestApproval | RequestDocumentation | RequestLease,
) => user.id === request.createdById

export const isVPBoss = (user: User) =>
  user.role && [roles.VP, roles.ZVP].includes(user.role)

export const isLegalBoss = (user: User) =>
  user.role && (isVPBoss(user) || user.role === roles.APO)

export const isRequestLegal = (
  user: User,
  request: RequestApproval | RequestDocumentation,
) =>
  user.role &&
  (isVPBoss(user) ||
    user.role === roles.P ||
    (user.role === roles.APO && isAssistant(user, request)))

export const isRequestLegalApproval = (user: User) =>
  user.role &&
  (isVPBoss(user) || user.role === roles.P || user.role === roles.APO)

export const canCreateRequestApproval = (user: User) =>
  isAdmin(user) || isUserRequestApproval(user)

export const canReadRequestApproval = (
  user: User,
  requestApproval: RequestApproval,
) =>
  isAdmin(user) ||
  isRequestLegal(user, requestApproval) ||
  isCreatedBy(user, requestApproval) ||
  user.role === roles.J ||
  user.role === roles.APO // APO can read all request approvals

export const canUpdateRequestApproval = (
  user: User,
  requestApproval: RequestApproval,
) =>
  isAdmin(user) ||
  (canReadRequestApproval(user, requestApproval) && user.role !== roles.J)

export const canCancelRequestApproval = (user: User) =>
  isAdmin(user) || isLegalBoss(user)

export const canConfirmRequestApprovalContract = (
  user: User,
  requestApproval: RequestApproval,
) => isAdmin(user) || isLegalBoss(user) || isCreatedBy(user, requestApproval)

export const canSendRequestApproval = (user: User) =>
  isAdmin(user) || isLegalBoss(user)

export const canDeclineRequestApprovalContract = (user: User) =>
  isAdmin(user) || isRequestLegalApproval(user)

export const canFinishRequestApprovalContract = (user: User) =>
  isAdmin(user) || isRequestLegalApproval(user)

export const canGenerateContract = (
  user: User,
  request: RequestApproval | RequestDocumentation,
) => {
  if (isAdmin(user)) return true
  if ('managerId' in request) {
    return isRequestLegal(user, request) || user.role === roles.APO
  }
  return isRequestLegalApproval(user)
}

export const canUpdateRequestApprovalCompanies = (
  user: User,
  requestApproval: RequestApproval,
) =>
  canUpdateRequestApproval(user, requestApproval) &&
  (isRequestLegalApproval(user) || isAdmin(user))

export const canConfirmRequestApprovalVP = (user: User) =>
  user.role && (isAdmin(user) || isLegalBoss(user))

export const canConfirmRequestApprovalUK = (
  user: User,
  requestApproval: RequestApproval,
) =>
  user.role &&
  (isAdmin(user) ||
    (user.role === roles.UK && isCreatedBy(user, requestApproval)))

export const isUserRequestDocumentation = (user: User) =>
  user.role &&
  (roles.REQUEST_DOCUMENTATION_USER_ROLES as readonly string[]).includes(
    user.role,
  )

export const canListRequestDocumentations = (user: User) =>
  isAdmin(user) || isUserRequestDocumentation(user)

export const canCreateRequestDocumentation = (user: User) =>
  isAdmin(user) || isUserRequestDocumentation(user)

export const canUpdateRequestDocumentation = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => {
  return (
    user.role !== roles.J &&
    (isAdmin(user) ||
      (user.role === roles.MKZ
        ? isCreatedBy(user, requestDocumentation)
        : isUserRequestDocumentation(user)))
  )
}

export const isUserContractTemplate = (user: User) =>
  user.role && [roles.VP, roles.ZVP].includes(user.role)

export const canCreateContractTemplate = (user: User) =>
  isAdmin(user) || isUserContractTemplate(user)

export const canListContractTemplate = (user: User) =>
  isAdmin(user) || isUserContractTemplate(user)

export const canUpdateContractTemplate = (user: User) =>
  isAdmin(user) || isUserContractTemplate(user)

export const canValidateContractTemplate = (user: User) =>
  isAdmin(user) || isUserContractTemplate(user)

export const isManager = (
  user: User,
  request: RequestDocumentation | RequestLease,
) => user.id === request.managerId

export const isDocumentationLaywer = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => user.id === requestDocumentation.assistantId

export const canConfirmRequestDocumentation = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => isAdmin(user) || isManager(user, requestDocumentation)

export const canPrepareRequestDocumentation = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => isAdmin(user) || isCreatedBy(user, requestDocumentation)

export const canCancelRequestDocumentation = (user: User) =>
  isAdmin(user) || isLegalBoss(user)

export const canSendRequestDocumentation = (
  user: User,
  requestDocumentation: RequestDocumentation,
) =>
  isAdmin(user) ||
  isLegalBoss(user) ||
  isDocumentationLaywer(user, requestDocumentation)

export const canDeclineRequestDocumentationContract = (
  user: User,
  requestDocumentation: RequestDocumentation,
) =>
  isCreatedBy(user, requestDocumentation) || isAdmin(user) || isLegalBoss(user)

export const canFinishRequestDocumentationContract = (
  user: User,
  requestDocumentation: RequestDocumentation,
) =>
  isAdmin(user) ||
  isLegalBoss(user) ||
  isDocumentationLaywer(user, requestDocumentation)

export const canChangeRequestDocumentationContract = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => isAdmin(user) || isCreatedBy(user, requestDocumentation)

export const canEvalRequestDocumentationContract = (
  user: User,
  requestDocumentation: RequestDocumentation,
) => isAdmin(user) || isManager(user, requestDocumentation)

export const isUserRequestLease = (user: User) =>
  user.role &&
  (roles.REQUEST_LEASE_USER_ROLES as readonly string[]).includes(user.role)

export const canListRequestLeases = (user: User) =>
  isAdmin(user) || isUserRequestLease(user)

export const canCreateRequestLease = (user: User) =>
  isAdmin(user) || isUserRequestLease(user)

export const isAssignee = (user: User, requestLease: RequestLease) =>
  user.id === requestLease.assigneeId

export const isRequestLeaseLegal = (user: User) =>
  user.role && (isVPBoss(user) || [roles.P, roles.APO].includes(user.role))

export const canReadAllRequestLeases = (user: User) =>
  canListRequestLeases(user) && user.role !== roles.RSO

export const canReadRequestLease = (user: User, requestLease: RequestLease) =>
  canReadAllRequestLeases(user) ||
  (user.role === roles.RSO && isCreatedBy(user, requestLease))

export const canUpdateRequestLease = (user: User, requestLease: RequestLease) =>
  isAdmin(user) ||
  (user.role === roles.MKZ
    ? isCreatedBy(user, requestLease)
    : isUserRequestLease(user))

export const canConfirmRequestLease = (
  user: User,
  requestLease: RequestLease,
) => isAdmin(user) || isManager(user, requestLease)

export const canPrepareRequestLease = (
  user: User,
  requestLease: RequestLease,
) => isAdmin(user) || isCreatedBy(user, requestLease)

export const canCancelRequestLease = (user: User) =>
  isAdmin(user) || isLegalBoss(user)

export const canSendRequestLease = (user: User, requestLease: RequestLease) =>
  isAdmin(user) || isLegalBoss(user) || isAssistant(user, requestLease)

const isRequestLeaseContractGenerationState = (
  requestLease: Pick<RequestLease, 'status'>,
) =>
  REQUEST_LEASE_CONTRACT_GENERATION_RULES.some(
    (rule) => rule.requestLeaseStatus === requestLease.status,
  )

export const canGenerateRequestLeaseContracts = (user: User) =>
  Boolean(isAdmin(user) || isRequestLeaseLegal(user))

export const canGenerateRequestLeaseContractForRequest = (
  user: User,
  requestLease: Pick<RequestLease, 'status'>,
) =>
  canGenerateRequestLeaseContracts(user) &&
  isRequestLeaseContractGenerationState(requestLease)

export const canGenerateRequestLeaseContract = (
  user: User,
  requestLease: Pick<RequestLease, 'status'>,
  requestLeaseContract: RequestLeaseContract,
) =>
  canGenerateRequestLeaseContractForRequest(user, requestLease) &&
  REQUEST_LEASE_CONTRACT_GENERATION_RULES.some(
    (rule) =>
      rule.requestLeaseStatus === requestLease.status &&
      rule.contractStatus === requestLeaseContract.contractStatus &&
      (rule.finalFileState === 'any' || !requestLeaseContract.finalFileId),
  )

export const canEditRequestLeaseContractPreparedFields = (
  user: User,
  requestLease: Pick<RequestLease, 'status'>,
  requestLeaseContract: RequestLeaseContract,
) =>
  requestLeaseContract.contractStatus === requestLeaseContractStatuses.NEW
    ? isRequestLeaseContractGenerationState(requestLease)
    : canGenerateRequestLeaseContract(user, requestLease, requestLeaseContract)

export const canFinishRequestLeaseContract = (
  user: User,
  requestLease: RequestLease,
) => isAdmin(user) || isLegalBoss(user) || isAssistant(user, requestLease)

export const canDeclineRequestLeaseContract = (
  user: User,
  requestLease: RequestLease,
) => isCreatedBy(user, requestLease) || isAdmin(user) || isLegalBoss(user)

export const canUploadSignedRequestLeaseContract = (
  user: User,
  requestLease: RequestLease,
  requestLeaseContract: RequestLeaseContract,
) =>
  requestLease.status === requestLeaseStatuses.PROCESSING &&
  requestLeaseContract.contractStatus ===
    requestLeaseContractStatuses.PENDING_SIGNATURE &&
  Boolean(
    user.role !== roles.J &&
      (isAdmin(user) ||
        (user.role === roles.MKZ
          ? isCreatedBy(user, requestLease)
          : isUserRequestLease(user))),
  )
